import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  getSectionFlags,
  getSectionsForApplication,
  type LoanSectionKey,
  type LoanSectionViewerContext,
} from '@/config/loanProductRegistry';
import {
  useGetLoanApplicationQuery,
  useLazyGetLoanSubmitReadinessQuery,
  usePatchLoanFocusProductMutation,
  usePatchLoanProductTypeMutation,
  useReinstateLoanApplicationMutation,
  useSubmitLoanApplicationMutation,
  useWithdrawLoanApplicationMutation,
  type LoanMissingField,
} from '@/apis/extended/loanApplicationApi';
import { TastePageHeader } from '@/components/Common/TastePageHeader';
import { useAuth } from '@/hooks/useAuth';
import { loanAttestationNeedsRecording } from '@/utils/loanApplicationAttestation';
import {
  linkedProductLabel,
  resolveProductId,
} from '@/utils/loanApplicationHub';
import { isSectionComplete } from '@/utils/propertyView/stageResolver';
import type { PropertyView } from '@/types/PropertyView';
import { AgencyLoanTermsSection } from './AgencyLoanTermsSection';
import { AssetsSection } from './AssetsSection';
import { BorrowerSection } from './BorrowerSection';
import { ChecklistSection } from './ChecklistSection';
import { DeclarationsSection } from './DeclarationsSection';
import { DemographicsSection } from './DemographicsSection';
import { DscrSection } from './DscrSection';
import { EmploymentSection } from './EmploymentSection';
import { InvestorProgramSection } from './InvestorProgramSection';
import { LiabilitiesSection } from './LiabilitiesSection';
import {
  LoanSectionFormProvider,
  useLoanSectionFormNav,
} from './LoanSectionFormContext';
import { PlaceholderSection } from './PlaceholderSection';
import { PropertySection } from './PropertySection';
import { RefinanceFieldsSection } from './RefinanceFieldsSection';
import { SignSection } from './SignSection';
import { SubmitMissingInfoModal } from './SubmitMissingInfoModal';
import { TrackRecordSection } from './TrackRecordSection';

type Props = {
  applicationId: string;
  /** Shown inside admin detail — tighter chrome */
  variant?: 'full' | 'embedded';
  /** Active deal-room property when embedded in a property stage */
  propertyContext?: PropertyView | null;
  /** Admin "View as user": the borrower's screens, nothing editable. */
  viewAs?: boolean;
  /** Open this section; `at` changes each time so a repeat request reopens it. */
  openSection?: { key: LoanSectionKey; at: number } | null;
};

export function LoanApplicationShell(props: Props) {
  return (
    <LoanSectionFormProvider>
      <LoanApplicationShellInner {...props} />
    </LoanSectionFormProvider>
  );
}

function LoanApplicationShellInner({
  applicationId,
  variant = 'full',
  propertyContext,
  viewAs = false,
  openSection,
}: Props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFocusSyncRef = useRef<string | null>(null);
  useEffect(() => {
    urlFocusSyncRef.current = null;
    setSelected(null);
  }, [applicationId]);
  const { isAdmin, user } = useAuth();
  const {
    data: application,
    isLoading,
    error,
  } = useGetLoanApplicationQuery(applicationId);
  const [submit, { isLoading: submitting }] =
    useSubmitLoanApplicationMutation();
  const [getSubmitReadiness, { isFetching: checkingReadiness }] =
    useLazyGetLoanSubmitReadinessQuery();
  const [missingFields, setMissingFields] = useState<LoanMissingField[]>([]);
  const [missingOpen, setMissingOpen] = useState(false);

  const submitToUnderwriting = async (appId: string) => {
    try {
      await submit(appId).unwrap();
      setMissingOpen(false);
      toast.success('Submitted to underwriting');
    } catch (e: unknown) {
      const data =
        e && typeof e === 'object' && 'data' in e
          ? (
              e as {
                data?: {
                  error?: string;
                  code?: string;
                  missingFields?: LoanMissingField[];
                };
              }
            ).data
          : undefined;
      if (data?.code === 'MISSING_FIELDS' && data.missingFields?.length) {
        setMissingFields(data.missingFields);
        setMissingOpen(true);
        return;
      }
      toast.error(data?.error || 'Submit failed');
    }
  };
  const [withdraw, { isLoading: withdrawing }] =
    useWithdrawLoanApplicationMutation();
  const [reinstate, { isLoading: reinstating }] =
    useReinstateLoanApplicationMutation();
  const [patchFocus] = usePatchLoanFocusProductMutation();
  const [patchProductType] = usePatchLoanProductTypeMutation();

  const [selected, setSelected] = useState<LoanSectionKey | null>(null);
  const embedded = variant === 'embedded';
  const confirmDiscard = useLoanSectionFormNav();
  const focusQueryParam = searchParams.get('focus');
  const sectionQueryParam = searchParams.get(
    'section'
  ) as LoanSectionKey | null;

  const selectSection = (key: LoanSectionKey) => {
    if (key === selected) return;
    if (!confirmDiscard()) return;
    setSelected(key);
  };

  const focusedProductId = useMemo(() => {
    if (!application) return '';
    return (
      resolveProductId(propertyContext?.productId ?? propertyContext?.id) ||
      resolveProductId(focusQueryParam) ||
      resolveProductId(application.focusProductId) ||
      resolveProductId(application.sourceProductId)
    );
  }, [application, propertyContext, focusQueryParam]);

  const sectionViewer: LoanSectionViewerContext = useMemo(
    () => ({
      loanEstimationType: user?.loanEstimationType,
      bypassInvestorGate: isAdmin,
      standaloneShell: !embedded,
      showPurchasePropertySection: Boolean(
        embedded ||
          focusQueryParam ||
          resolveProductId(application?.focusProductId) ||
          resolveProductId(application?.sourceProductId) ||
          propertyContext
      ),
    }),
    [
      user?.loanEstimationType,
      isAdmin,
      embedded,
      focusQueryParam,
      application?.focusProductId,
      application?.sourceProductId,
      propertyContext,
    ]
  );

  const sections = useMemo(
    () =>
      application ? getSectionsForApplication(application, sectionViewer) : [],
    [application, sectionViewer]
  );

  const sectionFlags = useMemo(
    () => (application ? getSectionFlags(application, sectionViewer) : null),
    [application, sectionViewer]
  );

  useEffect(() => {
    if (!openSection || !sections.some((s) => s.key === openSection.key)) {
      return;
    }
    if (confirmDiscard()) setSelected(openSection.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSection?.at]);

  useEffect(() => {
    if (!sections.length) return;
    setSelected((cur) => {
      if (cur && sections.some((s) => s.key === cur)) return cur;
      if (
        sectionQueryParam &&
        sections.some((s) => s.key === sectionQueryParam)
      ) {
        return sectionQueryParam;
      }
      return sections[0]?.key ?? 'borrower';
    });
  }, [sections, sectionQueryParam]);

  const borrowerMayEdit =
    application != null &&
    (application.status === 'draft' ||
      application.status === 'declined' ||
      (Boolean(application.borrowerEditsUnlocked) &&
        (application.status === 'submitted' ||
          application.status === 'in_review')));

  const readOnly =
    viewAs || (!isAdmin && application != null && !borrowerMayEdit);

  const attestationCurrent =
    application != null && !loanAttestationNeedsRecording(application);
  // "Sign again" only makes sense once they have signed; before that the
  // attestation is simply the last step, not something overdue.
  const attestationStale =
    application != null &&
    Boolean(application.signAttestation?.acceptedAt) &&
    loanAttestationNeedsRecording(application);
  // A read-only file whose signature went stale can still be signed again —
  // signing changes no answers, and otherwise nobody could clear it.
  const canResign =
    readOnly &&
    !viewAs &&
    attestationStale &&
    application.status !== 'withdrawn' &&
    application.status !== 'declined';

  useEffect(() => {
    const want = focusQueryParam;
    if (!application || !want || readOnly) return;
    const cur = resolveProductId(application.focusProductId);
    if (cur === want) {
      urlFocusSyncRef.current = want;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('focus');
          return next;
        },
        { replace: true }
      );
      return;
    }
    if (urlFocusSyncRef.current === want) return;
    urlFocusSyncRef.current = want;
    void patchFocus({ id: application._id, focusProductId: want })
      .unwrap()
      .then(() => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('focus');
            return next;
          },
          { replace: true }
        );
      })
      .catch(() => {
        urlFocusSyncRef.current = null;
        toast.error('Could not switch to that property.');
      });
  }, [application, readOnly, focusQueryParam, setSearchParams, patchFocus]);

  const activeSection = selected ?? sections[0]?.key ?? 'borrower';

  // Progress across the answer sections; "Review & sign" is the finish line,
  // not a section to count.
  const progress = useMemo(() => {
    if (!application) return null;
    const counted = sections.filter((s) => s.key !== 'sign');
    const done = new Set(
      counted
        .filter((s) => isSectionComplete(application, s.key))
        .map((s) => s.key)
    );
    const nextIncomplete = counted.find((s) => !done.has(s.key));
    // Count every section the sidebar lists, signing included, so "x of y"
    // matches what the borrower sees.
    const total = sections.length;
    const completed = done.size + (attestationCurrent ? 1 : 0);
    return {
      done,
      total,
      completed,
      percent: total ? Math.round((completed / total) * 100) : 0,
      nextIncomplete,
      incomplete: counted.filter((s) => !done.has(s.key)),
      allAnswered: !nextIncomplete,
    };
  }, [application, sections, attestationCurrent]);

  const activeIndex = sections.findIndex((s) => s.key === activeSection);
  const nextSection =
    activeIndex >= 0 && activeIndex < sections.length - 1
      ? sections[activeIndex + 1]
      : null;

  const currentLabel =
    sections.find((s) => s.key === activeSection)?.label ?? 'Section';

  // Every residential file is about one home: show it, or let the borrower
  // pick / type it when the file isn't linked to a property yet.
  const propertySectionVariant = 'propertyScoped' as const;

  const renderPanel = () => {
    if (!application) return null;
    switch (activeSection) {
      case 'property':
        return (
          <PropertySection
            application={application}
            readOnly={readOnly}
            propertyContext={propertyContext}
            variant={propertySectionVariant}
          />
        );
      case 'borrower':
        return (
          <BorrowerSection
            application={application}
            which="primary"
            readOnly={readOnly}
            showEntityQuestion={sectionFlags?.showEntityQuestionPrimary ?? true}
            onCoBorrowerAdded={() => setSelected('coBorrower')}
          />
        );
      case 'coBorrower':
        return (
          <BorrowerSection
            application={application}
            which="coBorrower"
            readOnly={readOnly}
            showEntityQuestion={sectionFlags?.showEntityQuestionPrimary ?? true}
            onCoBorrowerRemoved={() => setSelected('borrower')}
          />
        );
      case 'assets':
        return <AssetsSection application={application} readOnly={readOnly} />;
      case 'track':
        return (
          <TrackRecordSection application={application} readOnly={readOnly} />
        );
      case 'employment':
        return (
          <EmploymentSection application={application} readOnly={readOnly} />
        );
      case 'declarations':
        return (
          <DeclarationsSection application={application} readOnly={readOnly} />
        );
      case 'loanTerms':
        return (
          <AgencyLoanTermsSection
            application={application}
            readOnly={readOnly}
          />
        );
      case 'refinanceTerms':
        return (
          <RefinanceFieldsSection
            application={application}
            readOnly={readOnly}
          />
        );
      case 'investorProgram':
        return (
          <InvestorProgramSection
            application={application}
            readOnly={readOnly}
          />
        );
      case 'liabilities':
        return (
          <LiabilitiesSection application={application} readOnly={readOnly} />
        );
      case 'demographics':
        return (
          <DemographicsSection application={application} readOnly={readOnly} />
        );
      case 'dscr':
        if (application.productType !== 'dscr') {
          return (
            <PlaceholderSection
              title="DSCR"
              description="Not applicable for this product."
            />
          );
        }
        return <DscrSection application={application} readOnly={readOnly} />;
      case 'checklist':
        return (
          <ChecklistSection application={application} readOnly={readOnly} />
        );
      case 'sign':
        return (
          <SignSection
            application={application}
            readOnly={readOnly && !canResign}
            incompleteSections={progress?.incomplete ?? []}
            onGoToSection={selectSection}
          />
        );
      default:
        return <PlaceholderSection title={currentLabel} />;
    }
  };

  if (isLoading && !application) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading application…</Typography>
      </Box>
    );
  }

  if (error || !application) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Could not load this application. Return to the hub and try again.
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/loan')}>
          Back to hub
        </Button>
      </Box>
    );
  }

  const chrome = variant === 'full';

  return (
    <Box
      sx={{
        maxWidth: chrome ? 1320 : '100%',
        mx: chrome ? 'auto' : 0,
        px: chrome ? 2 : 0,
        py: chrome ? 3 : 0,
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {chrome && (
        <TastePageHeader
          title={
            application.flowProfile === 'refinance'
              ? 'Refinance application'
              : application.flowProfile === 'purchase'
              ? 'Purchase application'
              : 'Loan application'
          }
          subtitle={
            application.property?.subjectPropertyAddress ||
            'Answer each section, then review and sign to submit.'
          }
          actions={
            <>
              <Chip
                label={application.productType?.toUpperCase()}
                size="small"
              />
              <Chip
                label={application.status?.replace(/_/g, ' ')}
                size="small"
              />
              {application.lifecycleStage &&
                application.lifecycleStage !== 'shopping' && (
                  <Chip
                    label={application.lifecycleStage.replace(/_/g, ' ')}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              <Button variant="text" onClick={() => navigate('/loan')}>
                All applications
              </Button>
            </>
          }
        />
      )}

      {chrome &&
        application.flowProfile === 'purchase' &&
        application.productType === 'dscr' &&
        !readOnly &&
        application.status === 'draft' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This purchase file is still marked as DSCR from an older flow. For
            Conventional, FHA, VA, or USDA, switch the loan program—we will open
            the URLA-style sections.
            <Button
              sx={{ ml: 2 }}
              size="small"
              variant="contained"
              onClick={() => {
                void patchProductType({
                  id: application._id,
                  productType: 'conventional',
                })
                  .unwrap()
                  .then(() => {
                    toast.success(
                      'Switched to Conventional — complete Loan details.'
                    );
                  })
                  .catch((e: unknown) => {
                    const msg =
                      e && typeof e === 'object' && 'data' in e
                        ? String(
                            (e as { data?: { error?: string } }).data?.error
                          )
                        : 'Update failed';
                    toast.error(msg);
                  });
              }}
            >
              Use residential mortgage (Conventional)
            </Button>
          </Alert>
        )}

      {chrome &&
        (application.flowProfile === 'purchase' ||
          application.flowProfile === 'investor_dscr') &&
        Array.isArray(application.linkedPurchaseProductIds) &&
        application.linkedPurchaseProductIds.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Subject property:
            </Typography>
            {application.linkedPurchaseProductIds.map((p) => {
              const pid = resolveProductId(p);
              const focused =
                resolveProductId(application.focusProductId) === pid;
              return (
                <Chip
                  key={pid}
                  label={linkedProductLabel(p)}
                  size="small"
                  color={focused ? 'primary' : 'default'}
                  variant={focused ? 'filled' : 'outlined'}
                  onClick={() => {
                    if (readOnly || !pid) return;
                    void patchFocus({
                      id: application._id,
                      focusProductId: pid,
                    })
                      .unwrap()
                      .catch(() => {
                        toast.error('Could not switch property');
                      });
                  }}
                  sx={{ cursor: readOnly ? 'default' : 'pointer' }}
                />
              );
            })}
          </Box>
        )}

      {!readOnly && chrome && progress && progress.completed === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          We've filled in what you already told us. Answers about you, your
          income, assets and debts carry over to your other applications and
          update the estimates on your properties.
        </Alert>
      )}

      {chrome && !viewAs && application.status === 'withdrawn' && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={reinstating}
              onClick={async () => {
                try {
                  const reopened = await reinstate(application._id).unwrap();
                  toast.success(
                    reopened.status === 'draft'
                      ? 'Application reopened — finish it and submit when ready'
                      : 'Application reinstated and sent back to your loan team'
                  );
                } catch {
                  toast.error('Could not reopen the application');
                }
              }}
            >
              Reopen application
            </Button>
          }
        >
          You withdrew this application. Reopen it to pick up where you left off
          — nothing you entered was lost.
        </Alert>
      )}

      {readOnly && chrome && !viewAs && application.status !== 'withdrawn' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This file is read-only while it is in progress with underwriting.
        </Alert>
      )}

      {!readOnly &&
        chrome &&
        Boolean(application.borrowerEditsUnlocked) &&
        (application.status === 'submitted' ||
          application.status === 'in_review') && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Your loan team has turned editing back on for this file. Update any
            sections they requested, then keep an eye on your email for next
            steps.
          </Alert>
        )}

      {canResign && chrome && activeSection !== 'sign' && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => selectSection('sign')}
            >
              Sign again
            </Button>
          }
        >
          Your loan officer needs a current signature on this file. Your answers
          stay as submitted — just review and sign again.
        </Alert>
      )}

      {!readOnly && chrome && attestationStale && activeSection !== 'sign' && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => selectSection('sign')}
            >
              Sign again
            </Button>
          }
        >
          You changed answers after signing. Sign again before you submit.
        </Alert>
      )}

      {progress && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.5, sm: 2 }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                {application.status === 'withdrawn'
                  ? 'Withdrawn'
                  : application.status !== 'draft' &&
                    application.status !== 'declined'
                  ? `Submitted · ${application.status?.replace(/_/g, ' ')}`
                  : progress.allAnswered
                  ? attestationCurrent
                    ? 'Ready to submit'
                    : 'All sections answered — review and sign'
                  : `${progress.completed} of ${progress.total} sections complete`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress.percent}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            {!isAdmin && !readOnly && application.status === 'draft' && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {!progress.allAnswered && progress.nextIncomplete ? (
                  progress.nextIncomplete.key !== activeSection ? (
                    <Button
                      variant="contained"
                      onClick={() =>
                        progress.nextIncomplete &&
                        selectSection(progress.nextIncomplete.key)
                      }
                    >
                      Continue: {progress.nextIncomplete.label}
                    </Button>
                  ) : null
                ) : !attestationCurrent ? (
                  activeSection !== 'sign' ? (
                    <Button
                      variant="contained"
                      onClick={() => selectSection('sign')}
                    >
                      Review &amp; sign
                    </Button>
                  ) : null
                ) : (
                  <Button
                    variant="contained"
                    disabled={submitting || checkingReadiness}
                    onClick={async () => {
                      const blockingDocs = (application.checklist || []).filter(
                        (i) =>
                          i.requiredForSubmit !== false &&
                          (i.requiredForSubmit === true ||
                            i.required !== false) &&
                          !i.completed
                      );
                      if (blockingDocs.length > 0) {
                        toast.error(
                          `Complete required documents first: ${blockingDocs
                            .slice(0, 3)
                            .map((d) => d.label)
                            .join('; ')}`
                        );
                        return;
                      }
                      if (!attestationCurrent) {
                        toast.error(
                          'Open Attestation and sign before submitting to underwriting'
                        );
                        return;
                      }
                      // Ask the backend what's still missing and collect it in a modal
                      // rather than failing with a list of sections to go find.
                      try {
                        const readiness = await getSubmitReadiness(
                          application._id
                        ).unwrap();
                        if (readiness.missingFields.length > 0) {
                          setMissingFields(readiness.missingFields);
                          setMissingOpen(true);
                          return;
                        }
                      } catch {
                        // Readiness is a convenience — fall through to submit, whose
                        // MISSING_FIELDS error opens the same modal.
                      }
                      await submitToUnderwriting(application._id);
                    }}
                  >
                    Submit to underwriting
                  </Button>
                )}
                {/* Withdrawing an untouched draft means nothing — only offer
                    it once there is something to withdraw. */}
                {application.answersUpdatedAt ? (
                  <Button
                    variant="text"
                    color="inherit"
                    size="small"
                    disabled={withdrawing}
                    onClick={async () => {
                      try {
                        await withdraw(application._id).unwrap();
                        toast.success('Application withdrawn');
                      } catch {
                        toast.error('Withdraw failed');
                      }
                    }}
                    sx={{ color: 'text.secondary' }}
                  >
                    Withdraw
                  </Button>
                ) : null}
              </Stack>
            )}
          </Stack>
          <SubmitMissingInfoModal
            open={missingOpen}
            application={application}
            missingFields={missingFields}
            saving={submitting}
            onClose={() => setMissingOpen(false)}
            onSaved={() => submitToUnderwriting(application._id)}
          />
        </Paper>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: embedded
            ? { xs: '1fr', sm: 'minmax(0, 220px) minmax(0, 1fr)' }
            : { xs: '1fr', md: '260px minmax(0, 1fr)' },
          gap: 2,
          alignItems: 'start',
          minWidth: 0,
          width: '100%',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            overflow: 'hidden',
            minWidth: 0,
            maxHeight: embedded ? { xs: 280, sm: 'none' } : undefined,
            overflowY: embedded ? { xs: 'auto', sm: 'hidden' } : undefined,
          }}
        >
          <List dense disablePadding>
            {sections.map((s, i) => {
              const attestReminder = s.key === 'sign' && attestationStale;
              const sectionDone =
                s.key === 'sign'
                  ? attestationCurrent
                  : Boolean(progress?.done.has(s.key));
              return (
                <ListItemButton
                  key={s.key}
                  selected={activeSection === s.key}
                  onClick={() => selectSection(s.key)}
                >
                  <ListItemText
                    primary={
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <i
                          className={
                            sectionDone
                              ? 'fa-solid fa-circle-check'
                              : 'fa-light fa-circle'
                          }
                          aria-hidden
                          style={{
                            fontSize: 14,
                            color: sectionDone ? '#2e7d32' : undefined,
                            opacity: sectionDone ? 1 : 0.45,
                          }}
                        />
                        <span>{`${i + 1}. ${s.label}`}</span>
                        {attestReminder && (
                          <Chip
                            size="small"
                            color="warning"
                            label="Due"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: 3, minHeight: 320, minWidth: 0, overflow: 'hidden' }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {currentLabel}
          </Typography>
          {renderPanel()}
          {nextSection && (
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="outlined"
                onClick={() => selectSection(nextSection.key)}
                endIcon={<i className="fa-light fa-arrow-right" />}
              >
                Next: {nextSection.label}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
